import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { Link, useNavigate } from 'react-router-dom';

const Topbar = () => {
    const [newsData, setNewsData] = useState([]);
    const [homeLinkData, setHomeLinkData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        Promise.all([fetchNews(), fetchHomeLink()]);
    }, []);

    const fetchHomeLink = async () => {
        try {
            const response = await fetch(`${API_URL}api/get/Homelink`);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();

            if (Array.isArray(data) && data.length > 0) {
                setHomeLinkData(data[0]);
            }
        } catch (err) {
            console.error('Error fetching home link data:', err);
            setError('Failed to fetch featured content');
        }
    };

    const fetchNews = async () => {
        try {
            const response = await fetch(`${API_URL}api/index`);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();

            let processedData = [];
            if (Array.isArray(data)) {
                processedData = data;
            } else if (data.data && Array.isArray(data.data)) {
                processedData = data.data;
            } else if (data) {
                processedData = [data];
            }

            processedData = processedData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            processedData = processedData.filter(item => item && (item.link || item.image));
            setNewsData(processedData.slice(0, 15));
            setLoading(false);
        } catch (err) {
            console.error('Error fetching news:', err);
            setError('Failed to fetch news data');
            setLoading(false);
        }
    };

    const handleNavigation = (id) => {
        if (id) {
            window.scrollTo(0, 0);
            navigate(`/${id}`);
        }
    };

    const renderMedia = (item, section = 'first') => {
        if (!item) return null;

        if (section === 'first') {
            if (item.link) {
                return (
                    <iframe
                        src={item.link}
                        title={item.cetegorieName || 'News content'}
                        className="w-100"
                        width="600"
                        height="430"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                );
            } else if (item.image) {
                const imageUrl = item.image.startsWith('http') ? item.image : `${API_URL}/${item.image}`;
                return (
                    <img
                        src={imageUrl}
                        alt={item.cetegorieName || 'News thumbnail'}
                        className="w-100"
                        style={{ height: '430px', objectFit: 'cover' }}
                        onError={(e) => {
                            e.target.src = "https://placehold.co/600x430";
                        }}
                    />
                );
            }
        }

        // Rest of the renderMedia logic remains the same for other sections
        if (section === 'second' && item.image) {
            const imageUrl = item.image.startsWith('http') ? item.image : `${API_URL}${item.image}`;
            return (
                <img
                    src={imageUrl}
                    alt={item.cetegorieName || 'News thumbnail'}
                    className="w-25 h-25 object-cover"
                    style={{ minWidth: '100px', minHeight: '100px' }}
                    onError={(e) => {
                        e.target.src = "https://placehold.co/100x100";
                    }}
                />
            );
        }

        if (section === 'third') {
            if (item.link) {
                return (
                    <iframe
                        src={item.link}
                        title={item.cetegorieName || 'News content'}
                        className="w-100"
                        height="200"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                );
            } else if (item.image) {
                const imageUrl = item.image.startsWith('http') ? item.image : `${API_URL}${item.image}`;
                return (
                    <img
                        src={imageUrl}
                        alt={item.cetegorieName || 'News thumbnail'}
                        className="w-100"
                        style={{ height: '200px', objectFit: 'cover' }}
                        onError={(e) => {
                            e.target.src = "https://placehold.co/200x200";
                        }}
                    />
                );
            }
        }

        return null;
    };

    if (loading) return <div className="text-center py-4">Loading...</div>;
    if (error) return <div className="text-center py-4 text-danger">{error}</div>;
    if (!newsData.length && !homeLinkData) return <div className="text-center py-4">No news available</div>;

    return (
        <div className="container p-4" style={{ marginTop: '20px' }}>
            <div className="row">
                {/* Left column - Featured Content */}
                <div className="col-md-8">
                    {homeLinkData && (
                        <div className="mb-4">
                            <div>
                                {renderMedia(homeLinkData, 'first')}
                                <p style={{
                                    color: '#333',
                                    fontFamily: 'Noto Sans, sans-serif',
                                    fontSize: '15px',
                                    lineHeight: '21px',
                                    marginTop: '10px',
                                    height: '100px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: '4',
                                    WebkitBoxOrient: 'vertical',
                                    marginBottom: '10px'
                                }}>
                                    {homeLinkData.description || homeLinkData.short_description}
                                </p>
                                <button
                                    onClick={() => handleNavigation(homeLinkData.id)}
                                    className="btn btn-primary"
                                    style={{
                                        backgroundColor: '#007bff',
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '4px',
                                        color: 'white',
                                        fontFamily: 'Noto Sans, sans-serif',
                                        fontSize: '14px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    और पढ़ें
                                </button>
                            </div>
                            <hr className="my-2" />
                        </div>
                    )}
                </div>

                {/* Right column - News list */}
                <div className="col-md-4" style={{ height: '560px', overflowY: 'auto' }}>
                    {newsData.slice(0, 15).map((item, index) => (
                        <div key={item.id || index} className="d-flex mb-3">
                            {renderMedia(item, 'second')}
                            <div className="flex-grow-1 ms-2">
                                <p style={{
                                    color: '#333',
                                    fontFamily: 'Noto Sans, sans-serif',
                                    height: '45px',
                                    fontSize: '15px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: '4',
                                    WebkitBoxOrient: 'vertical'
                                }} className="font-weight-bold">
                                    {item.short_description}
                                </p>
                                <button
                                    onClick={() => handleNavigation(item.id)}
                                    className="btn btn-primary"
                                    style={{
                                        backgroundColor: '#007bff',
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '4px',
                                        color: 'white',
                                        fontFamily: 'Noto Sans, sans-serif',
                                        fontSize: '14px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    और पढ़ें
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Bottom row - News cards */}
            <div className="row mt-4">
                {newsData.slice(0, 4).map((item, index) => (
                    <div key={item.id || index} className="col-md-3 mb-4">
                        <div className="card">
                            {renderMedia(item, 'third')}
                            <div className="card-body">
                                <span style={{
                                    color: '#000',
                                    fontFamily: 'Noto Sans, sans-serif',
                                    fontSize: '15px',
                                    lineHeight: '21px',
                                    display: 'block',
                                    marginBottom: '8px'
                                }}>
                                    {item.cetegorieName}
                                </span>
                                <div>
                                    <p style={{
                                        color: '#333',
                                        height: '45px',
                                        fontFamily: 'Noto Sans, sans-serif',
                                        fontSize: '15px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: '4',
                                        WebkitBoxOrient: 'vertical',
                                        marginBottom: '10px'
                                    }}>
                                        {item.short_description}
                                    </p>
                                    <button
                                        onClick={() => handleNavigation(item.id)}
                                        className="btn btn-primary"
                                        style={{
                                            backgroundColor: '#007bff',
                                            border: 'none',
                                            padding: '8px 16px',
                                            borderRadius: '4px',
                                            color: 'white',
                                            fontFamily: 'Noto Sans, sans-serif',
                                            fontSize: '14px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        और पढ़ें
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Topbar;